import React, { useState } from "react";
import { ReactComponent as NoteIcon } from "../../../images/note-2_blue.svg";
import { ReactComponent as ImageIcon } from "../../../images/file_blue.svg";
import EmailAttachmentPreview from "../../common/email-viewer/EmailAttachmentPreview";
import {
  MimeAttachmentContent,
  canPreviewAttachment,
  downloadAttachmentBlob,
  fileTypeToMimeType,
  resolveFileExtension,
} from "../../common/email-viewer/emailAttachmentUtils";
import "../../common/email-viewer/emailViewer.scss";

export interface PickupDocument {
  src: string;
  fileType: string;
  alt: string;
  type: string;
}

interface PickupDocImageViewerProps {
  documents: PickupDocument[];
}

const IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"];

export const PickupDocImageViewer: React.FC<PickupDocImageViewerProps> = ({ documents }) => {
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  const attachments: MimeAttachmentContent[] = documents.map((doc) => {
    const ext = resolveFileExtension(doc.fileType, doc.src);
    return {
      filename: `${doc.type}.${ext}`,
      mimeType: fileTypeToMimeType(ext),
      url: doc.src,
    };
  });

  const previewAttachment = previewIndex != null ? attachments[previewIndex] : null;

  if (!documents.length) {
    return null;
  }

  return (
    <div className="xgs-email-viewer">
      <div className="xgs-email-viewer__attachments">
        <div className="xgs-email-viewer__attachments-title">Documents</div>
        <div className="xgs-email-viewer__attachments-list">
          {attachments.map((attachment, index) => {
            const isPreviewing = previewIndex === index;
            const canPreview = canPreviewAttachment(attachment.mimeType, attachment.filename);
            return (
              <div
                key={`${attachment.filename}-${index}`}
                className={`xgs-email-viewer__attachment${
                  isPreviewing ? " xgs-email-viewer__attachment--active" : ""
                }`}
              >
                <div className="xgs-email-viewer__attachment-icon">
                  {IMAGE_TYPES.includes(attachment.mimeType) ? <ImageIcon /> : <NoteIcon />}
                </div>
                <div className="xgs-email-viewer__attachment-info">
                  <span className="xgs-email-viewer__attachment-name">{documents[index].type}</span>
                  <span className="xgs-email-viewer__attachment-ext">
                    {resolveFileExtension(documents[index].fileType, documents[index].src).toUpperCase()}
                  </span>
                </div>
                <div className="xgs-email-viewer__attachment-actions">
                  {canPreview && (
                    <button
                      type="button"
                      className="xgs-email-viewer__attachment-action"
                      onClick={() => setPreviewIndex(isPreviewing ? null : index)}
                    >
                      {isPreviewing ? "Hide" : "View"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="xgs-email-viewer__attachment-action"
                    onClick={() => downloadAttachmentBlob(attachment)}
                  >
                    Download
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {previewAttachment && (
        <EmailAttachmentPreview
          attachment={previewAttachment}
          onClose={() => setPreviewIndex(null)}
        />
      )}
    </div>
  );
};
